import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ApiError, Abfallart, AbfallartenListe } from "../../api";
import { useGetAbfallart, useSucheAbfallarten } from "../../client/hooks";

interface AbfallartenSelectProps extends FieldProps {
  label: string;
  required?: boolean;
  showKeyHeader?: boolean;
}

function AbfallartenSelect({
  label,
  required,
  showKeyHeader,
  field,
  form,
  meta,
}: AbfallartenSelectProps) {
  const searchAbfallarten = useSucheAbfallarten();
  const ladeAbfallart = useGetAbfallart();
  const [optionen, setOptionen] = useState<ReadonlyArray<Abfallart>>([]);
  const [inputValue, setInputValue] = useState(field.value);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    form.setFieldValue(field.name, inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      ladeAbfallart(field.value)
        .then((abfallart) => {
          form.setFieldValue(field.name, abfallart);
          setInputValue(abfallart);
        })
        .catch((error) => {
          console.error("Error loading abfallart:", error);
        });
    }
  }, [field.value]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: AbfallartenListe) => void
        ) =>
          searchAbfallarten({
            search: request.input,
            page: 0,
            limit: 20,
            sort: "name",
          })
            .then((results) => {
              callback(undefined, results);
            })
            .catch((error) => callback(error)),
        400
      ),
    [searchAbfallarten]
  );

  useEffect(() => {
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      field={field}
      form={form}
      meta={meta}
      filterSelectedOptions={true}
      filterOptions={(x) => x} // Deaktiviert die Filterlogik, damit komplett nur in dem Backend gesucht werden kann.
      options={optionen}
      getOptionLabel={(option: string | Abfallart) => {
        if (typeof option === "string") {
          return "";
        } else {
          return showKeyHeader ? option.key ?? option.name : option.name;
        }
      }}
      onChange={(event, value) => {
        setInputValue(value);
        const abfallart = value as Abfallart;
        if (abfallart) {
          form.setFieldValue("abfallartKey", abfallart.key);
        }
      }}
      onInputChange={(event, value) => {
        setSearchValue(value);
      }}
      renderOption={(props, option: Abfallart) => {
        return (
          <ListItem {...props} key={option.id}>
            {showKeyHeader ? (
              <ListItemText primary={option.key} secondary={option.name} />
            ) : (
              <ListItemText primary={option.name} secondary={option.key} />
            )}
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default AbfallartenSelect;
